export interface VisibleCompatibleModel {
  readonly kind: "model";
  readonly value: string;
  readonly isMatch: boolean;
}

export interface VisibleCompatibleModelsEllipsis {
  readonly kind: "ellipsis";
  readonly value: "...";
}

export type VisibleCompatibleModelsItem =
  | VisibleCompatibleModel
  | VisibleCompatibleModelsEllipsis;

const MIN_TOKEN_LENGTH = 2;
const MATCH_CONTEXT_SIZE = 1;
const DEFAULT_VISIBLE_MODEL_COUNT = 5;

export function getSearchTokens(query: string | undefined): string[] {
  if (!query) {
    return [];
  }

  return query
    .trim()
    .split(/\s+/)
    .map(normalizeCompatibleModelSearchText)
    .filter((token) => token.length >= MIN_TOKEN_LENGTH);
}

export function findFirstMatchingModelIndex(
  models: readonly string[],
  tokens: readonly string[],
): number | null {
  for (const token of tokens) {
    const matchIndex = models.findIndex((model) =>
      normalizeCompatibleModelSearchText(model).includes(token),
    );

    if (matchIndex >= 0) {
      return matchIndex;
    }
  }

  return null;
}

function normalizeCompatibleModelSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, "");
}

export function getVisibleCompatibleModels(
  models: readonly string[],
  matchIndex: number | null,
): VisibleCompatibleModelsItem[] {
  if (models.length === 0) {
    return [];
  }

  if (matchIndex !== null && matchIndex >= 0 && matchIndex < models.length) {
    return buildModelWindow(models, matchIndex);
  }

  const visibleModels = models.slice(0, DEFAULT_VISIBLE_MODEL_COUNT).map(
    (model): VisibleCompatibleModel => ({
      kind: "model",
      value: model,
      isMatch: false,
    }),
  );

  if (models.length > DEFAULT_VISIBLE_MODEL_COUNT) {
    return [...visibleModels, { kind: "ellipsis", value: "..." }];
  }

  return visibleModels;
}

function buildModelWindow(
  models: readonly string[],
  matchIndex: number,
): VisibleCompatibleModelsItem[] {
  const startIndex = Math.max(0, matchIndex - MATCH_CONTEXT_SIZE);
  const endIndex = Math.min(models.length, matchIndex + MATCH_CONTEXT_SIZE + 1);
  const visibleModels = models.slice(startIndex, endIndex).map(
    (model, relativeIndex): VisibleCompatibleModel => ({
      kind: "model",
      value: model,
      isMatch: startIndex + relativeIndex === matchIndex,
    }),
  );

  return [
    ...(startIndex > 0 ? [{ kind: "ellipsis" as const, value: "..." as const }] : []),
    ...visibleModels,
    ...(endIndex < models.length
      ? [{ kind: "ellipsis" as const, value: "..." as const }]
      : []),
  ];
}
